import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { batchingService } from '../../pages/BatchReportingPage/services/batchingService';
import type { BatchingStudy } from '../../pages/BatchReportingPage/services/batchingService';

export interface BatchEditState {
  /** Whether we are in batch editing mode */
  isBatchMode: boolean;
  /** Mongo _id of the batching document */
  batchId: string | null;
  /** All studies remaining in this batch */
  studies: BatchingStudy[];
  /** Total number of studies that were originally in the batch */
  totalStudies: number;
  /** Index of the study currently being edited (0-based) */
  currentIndex: number;
  /** The descriptive text for the current study */
  currentDescriptive: string | null;
  /** The patient id for the current study */
  currentPatientId: string | null;
  /** The mongo_template_id for the current study (if matched) */
  currentMongoTemplateId: string | null;
  /** Id estable del estudio actual; su borrador lleva `batchRef` con él */
  currentStudyId: string | null;
  /** Whether data is being loaded */
  isLoading: boolean;
}

export interface BatchEditContextValue extends BatchEditState {
  /** Initialize batch mode with a batchId — fetches data from API */
  initBatch: (batchId: string) => Promise<void>;
  /** Tras enviar: quita del lote el estudio enviado, por su `studyId` (nunca por posición) */
  onStudyGenerated: (studyId: string) => Promise<void>;
  /** Called after "send"/save — advances to the next study or exits batch mode */
  advanceToNextStudy: () => boolean;
  /** Exit batch mode */
  exitBatch: () => void;
  /** Label for the header: "Lote 1 de 5" */
  batchLabel: string;
}

const defaultState: BatchEditContextValue = {
  isBatchMode: false,
  batchId: null,
  studies: [],
  totalStudies: 0,
  currentIndex: 0,
  currentDescriptive: null,
  currentPatientId: null,
  currentMongoTemplateId: null,
  currentStudyId: null,
  isLoading: false,
  initBatch: async () => {},
  onStudyGenerated: async () => {},
  advanceToNextStudy: () => false,
  exitBatch: () => {},
  batchLabel: '',
};

const BatchEditContext = createContext<BatchEditContextValue>(defaultState);

export function BatchEditProvider({ children }: { children: ReactNode }) {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [studies, setStudies] = useState<BatchingStudy[]>([]);
  const [totalStudies, setTotalStudies] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Track which original index we've processed (for the "Lote X de Y" label)
  const processedCountRef = useRef(0);
  // Copias síncronas: `advanceToNextStudy` se llama justo después de `onStudyGenerated`, antes de re-renderizar.
  const batchIdRef = useRef<string | null>(null);
  const studiesRef = useRef<BatchingStudy[]>([]);
  const applyStudies = useCallback((next: BatchingStudy[]) => {
    studiesRef.current = next;
    setStudies(next);
  }, []);

  const isBatchMode = batchId !== null;
  const currentStudy = studies.length > 0 ? studies[0] : null;
  const currentDescriptive = currentStudy?.descriptivo ?? null;
  const currentPatientId = currentStudy?.id_paciente ?? null;
  const currentMongoTemplateId = currentStudy?.mongo_template_id ?? null;
  const currentStudyId = currentStudy?.studyId ?? null;

  // Label: "Lote 1 de 5", "Lote 2 de 5", etc.
  const batchLabel = isBatchMode
    ? `Lote ${processedCountRef.current + 1} de ${totalStudies}`
    : '';

  const initBatch = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const batch = await batchingService.getById(id);
      // El editor se remonta en cada estudio y vuelve a pedir el lote: se conserva el avance.
      const sameBatch = batchIdRef.current === id;
      batchIdRef.current = id;
      setBatchId(id);
      applyStudies(batch.studies);
      if (!sameBatch) {
        setTotalStudies(batch.studies.length);
        setCurrentIndex(0);
        processedCountRef.current = 0;
      }
    } catch (error) {
      console.error('Error loading batch:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [applyStudies]);

  // El backend ya sacó del lote el estudio enviado (H-19): aquí solo se refresca. Si sigue
  // (borrador sin `batchRef`), se borra como antes. Si algo falla, el error sube y no se avanza.
  const onStudyGenerated = useCallback(async (studyId: string) => {
    const id = batchIdRef.current;
    if (!id) return;
    const batch = await batchingService.findById(id);
    if (batch === null) { applyStudies([]); return; }
    if (!batch.studies.some((s) => s.studyId === studyId)) { applyStudies(batch.studies); return; }
    const result = await batchingService.removeStudy(id, studyId);
    applyStudies(result === null ? [] : result.studies);
  }, [applyStudies]);

  const advanceToNextStudy = useCallback((): boolean => {
    processedCountRef.current += 1;

    if (studiesRef.current.length === 0) {
      // No more studies, batch is done
      batchIdRef.current = null;
      setBatchId(null);
      applyStudies([]);
      setTotalStudies(0);
      setCurrentIndex(0);
      processedCountRef.current = 0;
      return false;
    }

    // onStudyGenerated ya quitó el enviado: studies[0] es el siguiente.
    // `currentIndex` cambia la key del editor, que se remonta limpio para el nuevo estudio.
    setCurrentIndex(prev => prev + 1);
    return true;
  }, [applyStudies]);

  const exitBatch = useCallback(() => {
    batchIdRef.current = null;
    setBatchId(null);
    applyStudies([]);
    setTotalStudies(0);
    setCurrentIndex(0);
    processedCountRef.current = 0;
  }, [applyStudies]);

  const value = useMemo(() => ({
        isBatchMode,
        batchId,
        studies,
        totalStudies,
        currentIndex,
        currentDescriptive,
        currentPatientId,
        currentMongoTemplateId,
        currentStudyId,
        isLoading,
        initBatch,
        onStudyGenerated,
        advanceToNextStudy,
        exitBatch,
        batchLabel,
      }), [isBatchMode, batchId, studies, totalStudies, currentIndex,
           currentDescriptive, currentPatientId, currentMongoTemplateId, currentStudyId,
           isLoading, initBatch, onStudyGenerated, advanceToNextStudy,
           exitBatch, batchLabel]);

  return (
    <BatchEditContext.Provider
      value={value}
    >
      {children}
    </BatchEditContext.Provider>
  );
}

export function useBatchEdit(): BatchEditContextValue {
  return useContext(BatchEditContext);
}
